/**
 * Board Column Component
 * Single column in the kanban board
 */
import { useDroppable } from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { ActionItem } from '@/types/actions'
import { ActionItemCard } from './ActionItemCard'

interface BoardColumnProps {
  title: string
  status: string
  items: ActionItem[]
  onCardClick: (actionId: string) => void
  onCreateClick: () => void
}

export function BoardColumn({ title, status, items, onCardClick, onCreateClick }: BoardColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  })

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col h-full bg-muted/50 rounded-lg transition-colors ${
        isOver ? 'ring-2 ring-primary bg-primary/5' : ''
      }`}
    >
      {/* Column Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">{title}</h3>
          <span className="text-sm text-muted-foreground bg-background px-2 py-1 rounded">
            {items.length}
          </span>
        </div>
      </div>
      
      {/* Cards Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3 min-h-[100px]">
          {items.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No items in {title.toLowerCase()}
            </div>
          ) : (
            items.map((item) => (
              <ActionItemCard
                key={item.id}
                item={item}
                onClick={() => onCardClick(item.id)}
              />
            ))
          )}
        </div>
      </ScrollArea>
      
      {/* Add Button */}
      <div className="p-4 border-t">
        <Button
          variant="outline"
          className="w-full"
          onClick={onCreateClick}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>
    </div>
  )
}
